import { Inject, Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';

import {
    Account,
    TransactionType,
    OrderType,
    OrderDuration,
    DeskRouteCode,
    AccountType,
    DeskRouteTimeZone,
    TradingDayInfo,
    TradeCommission
} from '../../models/common/trading-common.model';

import { EquityTradingOrder } from './../../models/equity/trading-equity.model';
import { TradingEntitlements } from './../../models/common/trading-entitlements';
import { EquityTradingServiceStrategy } from './equity-trading-service-strategy.service';

@Injectable()
export class EquityTradingService {
    private commissionOverrideSubject = new BehaviorSubject<TradeCommission | null>(null);
    commissionOverrideSubject$ = this.commissionOverrideSubject.asObservable();

    constructor(@Inject(EquityTradingServiceStrategy) private strategies: Array<EquityTradingServiceStrategy>) {

    }

    private getStrategy(platform: string): EquityTradingServiceStrategy {
        return this.strategies.find((strategy) => {
            return strategy.support(platform);
        });
    }

    public commissionOverrideChangeNotification(tradeCommission: TradeCommission) {
        this.commissionOverrideSubject.next(tradeCommission);
    }

    public getTransactionTypes(platform: string, account: Account, selectedSession: string): TransactionType[] {
        return this.getStrategy(platform).getTransactionTypes(account, selectedSession);
    }

    public getOrderTypes(platform: string, entitlements: TradingEntitlements, isNonDollar: boolean, selectedSession: string,
                          hasCodeValueExchange: boolean, qty: number, dollarOrFractional: boolean, transactionType: TransactionType): OrderType[] {
        return this.getStrategy(platform).getOrderTypes(entitlements, isNonDollar, selectedSession, hasCodeValueExchange, qty, dollarOrFractional, transactionType);
    }

    public getDurations(platform: string, selectedSession: string, isNonDollar: boolean, tradingDayInfo: TradingDayInfo, transactionType: TransactionType,
                          orderType?: string, ordQty?: number, dollarOrFractional?: boolean): OrderDuration[] {
        return this.getStrategy(platform).getDurations(selectedSession, tradingDayInfo, isNonDollar, transactionType, orderType, ordQty, dollarOrFractional);
    }

    public accountTypes(
        platform: string, isNonDollar: boolean, account: Account, transactionType: TransactionType, isGTCOrder: boolean, symAccountType: AccountType
    ): AccountType[] {
        return this.getStrategy(platform).accountTypes(account, isNonDollar, transactionType, isGTCOrder, symAccountType);
    }

    public getShortSaleRebate(platform: string, account: string, symbol: string): Observable<any> {
        return this.getStrategy(platform).getShortSaleRebate(account, symbol);
    }

    public getDeskRouteTimeZone(platform: string, account?: Account): DeskRouteTimeZone[] {
        return this.getStrategy(platform).getDeskRouteTimeZone();
    }

    public getDeskCode(platform: string, account?: Account): DeskRouteCode[] {
        return this.getStrategy(platform).getDeskCode();
    }

    public reviewEquityOrder(platform: string, equityTradingOrder: EquityTradingOrder): Observable<any> {
        return this.getStrategy(platform).reviewEquityOrder(equityTradingOrder);
    }

    public submitEquityOrder(platform: string, equityTradingOrder: EquityTradingOrder): Observable<any> {
        return this.getStrategy(platform).submitEquityOrder(equityTradingOrder);
    }

    public correctEquityOrder(platform: string, equityTradingOrder: EquityTradingOrder): Observable<any> {
        return this.getStrategy(platform).correctEquityOrder(equityTradingOrder);
    }

    public cancelEquityOrder(platform: string, acctNum: string, ordDtlKey: string, subRequestTypeCode: string): Observable<any> {
        return this.getStrategy(platform).cancelEquityOrder(acctNum, ordDtlKey, subRequestTypeCode);
    }

    public getCodeValuesExchangeList(platform: string): Observable<any> {
        return this.getStrategy(platform).getCodeValuesExchangeList();
    }

    public getCodeValuesFloorInstructions(platform: string): Observable<any> {
        return this.getStrategy(platform).getCodeValuesFloorInstructions();
    }
}
